import { useEffect, useState } from 'react';
import { netsuiteItemsService } from '../services/netsuite-items.service';
import { NetsuiteItemResponse } from '../types/netsuite-items.types';

/** Client NetSuite catalog fetch state */
export interface ClientNetsuiteCatalog {
  /** BOM-visible NetSuite items for the client */
  items: NetsuiteItemResponse[];
  isLoading: boolean;
}

/**
 * Fetches the NetSuite item catalog for a client (BOM-visible items only).
 * Returns an empty catalog when no client ID is provided.
 *
 * @param clientId - Client ID, or empty string to skip fetching
 * @returns Catalog items and loading state
 */
export const useClientNetsuiteCatalog = (clientId: string): ClientNetsuiteCatalog => {
  const [items, setItems] = useState<NetsuiteItemResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setItems([]);
      return;
    }
    let isCancelled = false;
    const fetchItems = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const data = await netsuiteItemsService.getItemsByClient(clientId);
        if (!isCancelled) {
          setItems(data.filter(item => item.show_in_bom));
        }
      } catch (error) {
        console.error('Failed to fetch netsuite items:', error);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    fetchItems();
    return () => {
      isCancelled = true;
    };
  }, [clientId]);

  return { items, isLoading };
};
